import { CheckboxAtom, Icon, Stack, Tooltip, Typography } from '@ui-components/neo.tax';
import { ReactComponent as OpenLink } from '@ui-components/neo.tax/dist/icons/open-link.svg';
import React, { ReactElement, memo } from 'react';

import { AscProjectTpTicketsView, RdProjectTpTicketsView } from '@neo.tax/shared/graphql';

import { ReasoningTrigger } from '../ticket-grouping-list/ReasoningTrigger';
import {
  DisabledDot,
  TicketRowContainer,
  TicketRowMainContent,
  TicketRowDetails,
  TicketRowTitle,
  TicketRowContributorName,
} from './tickets-list.styled';

// The matching fields exist only on the ASC view (CPC per-ticket matching reasoning), so they
// cannot join the Pick over the Asc | Rd union — intersect them in as optionals instead. RD rows
// simply never populate them.
export type TicketRowTicketType = Pick<
  AscProjectTpTicketsView | RdProjectTpTicketsView,
  'id' | 'identifier' | 'ticketUrl' | 'name' | 'contributorName'
> &
  Partial<Pick<AscProjectTpTicketsView, 'matchingConfidence' | 'matchingReasoning' | 'matchingMethod'>>;

interface TicketRowProps {
  item: TicketRowTicketType & {
    selected: boolean;
    onSelectedChange?: (selected: boolean) => void;
    disabled?: boolean;
    isGroupingSelected?: boolean;
  };
}

const TicketRow = ({ item: ticket }: TicketRowProps): ReactElement => {
  const {
    contributorName,
    disabled,
    identifier,
    isGroupingSelected,
    matchingReasoning,
    name,
    onSelectedChange,
    selected,
    ticketUrl,
  } = ticket;
  return (
    <TicketRowContainer paddingX={4} paddingY={1} gap={1} direction="column">
      <TicketRowMainContent alignItems="center" direction="row" gap={2}>
        {disabled ? (
          <DisabledDot />
        ) : (
          <CheckboxAtom
            checked={selected}
            onChange={onSelectedChange && ((e): void => onSelectedChange(e.target.checked))}
            disabled={isGroupingSelected}
          />
        )}
        <TicketRowDetails direction="column" gap={2}>
          <Stack justifyContent="space-between" alignItems="center" gap={2}>
            <TicketRowTitle title={name ?? undefined} fontSize="sm" fontWeight="medium">
              {/* We don't actually think this is ever null, but so far we can't prove it never is. */}
              {name ?? '-- No Ticket Name --'}
            </TicketRowTitle>
            {ticketUrl && (
              <a href={ticketUrl} target="_blank" rel="noreferrer">
                <Icon node={<OpenLink />} size={3} colorVariant="none" />
              </a>
            )}
          </Stack>
        </TicketRowDetails>
      </TicketRowMainContent>
      <Stack direction="row" gap={2} alignItems="center" paddingX={6}>
        <Tooltip text={contributorName}>
          <TicketRowContributorName fontSize="xs" value={contributorName}>
            {contributorName}
          </TicketRowContributorName>
        </Tooltip>

        <Typography fontSize="xs" value={identifier}>
          {identifier}
        </Typography>

        {/* Per-ticket matching reasoning (CPC parentless-ticket matching). The backend nulls the
            field for non-CPC companies and for tickets moved away from where matching routed
            them, so presence is the render signal — same pattern as the grouping header. */}
        {matchingReasoning && <ReasoningTrigger reasoning={matchingReasoning} />}
      </Stack>
    </TicketRowContainer>
  );
};

export const MemoizedTicketRow = memo(TicketRow);
